import type { DaySchedule } from "@/lib/api/types";
import { DAYS, formatScheduleTime } from "@/lib/utils/schedule";
import { cn } from "@/lib/utils";

const DAY_LABELS: Record<string, string> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

function slotsEqual(a: DaySchedule[], b: DaySchedule[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Read-only weekly schedule display — day pills + grouped hour ranges. No editing affordances. */
export function DealScheduleSummary({ schedule }: { schedule: Record<string, DaySchedule[]> }) {
  const activeDays = DAYS.filter((d) => (schedule[d] ?? []).length > 0);

  const lines: { days: string[]; slots: DaySchedule[] }[] = [];
  for (const day of activeDays) {
    const slots = schedule[day];
    const last = lines[lines.length - 1];
    if (last && slotsEqual(last.slots, slots)) {
      last.days.push(day);
    } else {
      lines.push({ days: [day], slots });
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-1">
        {DAYS.map((day) => (
          <span
            key={day}
            className={cn(
              "flex-1 h-7 rounded-md flex items-center justify-center text-[10px] font-semibold",
              activeDays.includes(day)
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground/50"
            )}
          >
            {DAY_LABELS[day]}
          </span>
        ))}
      </div>
      {lines.length === 0 ? (
        <p className="text-xs text-muted-foreground">No active days</p>
      ) : (
        <div className="space-y-0.5">
          {lines.map((line, i) => (
            <p key={i} className="text-xs text-muted-foreground">
              {line.days.map((d) => DAY_LABELS[d]).join(", ")}:{" "}
              {line.slots
                .map((s) => `${formatScheduleTime(s.open)} – ${formatScheduleTime(s.close)}`)
                .join(", ")}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
